import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import s from '../styles/modules/ClientOtp.module.scss';

const isClientRequest = (config) => {
  if (!config || !config.url) return false;

  return config.url.replace(process.env.NEXT_PUBLIC_API, '').split('?')[0] === '/client';
};

const getRequestData = (data) => {
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  }

  return data;
};

const ClientOtp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [phone, setPhone] = useState('');
  const [codeCells, setCodeCells] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const resolver = useRef(null);
  const inputRefs = useRef([]);
  const code = codeCells.join('');

  const requestCode = (clientPhone, initialError = '') =>
    new Promise((resolve) => {
      resolver.current = resolve;
      setPhone(clientPhone);
      setCodeCells(['', '', '', '']);
      setError(initialError);
      setIsVisible(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    });

  const closeModal = () => {
    setIsVisible(false);
    if (resolver.current) {
      resolver.current(null);
      resolver.current = null;
    }
  };

  const submitCode = () => {
    if (code.length !== 4) {
      setError('Введите 4 цифры из SMS');
      return;
    }

    setIsVisible(false);
    if (resolver.current) {
      resolver.current(code);
      resolver.current = null;
    }
  };
  const setCodeValue = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      const nextCode = [...codeCells];
      nextCode[index] = '';
      setCodeCells(nextCode);
      setError('');
      return;
    }

    const nextCode = [...codeCells];
    digits
      .slice(0, 4 - index)
      .split('')
      .forEach((digit, digitIndex) => {
        nextCode[index + digitIndex] = digit;
      });

    setCodeCells(nextCode);
    setError('');

    const nextIndex = Math.min(index + digits.length, 3);
    inputRefs.current[nextIndex]?.focus();
  };
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeCells[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'Enter' && code.length === 4) {
      submitCode();
    }
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      async (response) => {
        const data = response && response.data;

        if (
          data?.status !== 'otp_required' ||
          !data.otpToken ||
          !isClientRequest(response.config) ||
          response.config.__otpRetry
        ) {
          return response;
        }

        const requestData = getRequestData(response.config.data);
        let otpCode = await requestCode(requestData.clientPhone);

        if (!otpCode) {
          return {
            ...response,
            data: {
              status: 'cancelled',
              text: 'Отправка заявки отменена'
            }
          };
        }

        while (otpCode) {
          try {
            return await axios({
              ...response.config,
              __otpRetry: true,
              data: {
                ...requestData,
                otpToken: data.otpToken,
                otpCode
              }
            });
          } catch (requestError) {
            otpCode = await requestCode(
              requestData.clientPhone,
              requestError.response?.data?.text || 'Не удалось подтвердить заявку'
            );
          }
        }

        return {
          ...response,
          data: {
            status: 'cancelled',
            text: 'Отправка заявки отменена'
          }
        };
      },
      (requestError) => {
        if (requestError.config?.__otpRetry) {
          return Promise.reject(requestError);
        }

        if (isClientRequest(requestError.config)) {
          return requestError.response;
        }

        return Promise.reject(requestError);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  return (
    <Modal
      title="Подтвердите заявку"
      button="Отправить заявку"
      buttonDisabled={code.length !== 4}
      action={submitCode}
      visibility={isVisible}
      onClose={closeModal}
    >
      <div className={s.description}>
        Мы отправили код подтверждения на номер +{phone}. Введите его, чтобы заявка ушла в работу.
      </div>
      <div className={s.code} aria-label="Код из SMS">
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            className={[s.codeInput, error && s.error].filter(Boolean).join(' ')}
            value={codeCells[index]}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            onChange={(e) => setCodeValue(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) => {
              e.preventDefault();
              setCodeValue(index, e.clipboardData.getData('text'));
            }}
          />
        ))}
      </div>
      {error && <div className={s.errorText}>{error}</div>}
    </Modal>
  );
};

export default ClientOtp;
